import React, { FC, useState } from 'react';
import { cx } from '@emotion/css';
import { useTranslation } from 'next-i18next';

import { getDesignSystemIcon } from '@phoenix/design-system/frontend/util/path.utils';
import { PhoenixIcon } from '@phoenix/design-system/frontend/ui/phoenix-icon/phoenix-icon';

const RATING_VALUES = [1, 2, 3, 4, 5];
const starRatingIcon = getDesignSystemIcon('icon-star-16.svg');

export const FeedbackStarsRating: FC<
  React.PropsWithChildren<{
    ratingValue?: number;
    onClickRating: (ratingValue: number) => void;
    className?: string;
    feedbackLabels?: [string, string];
  }>
> = ({ ratingValue = 0, onClickRating, className, feedbackLabels }) => {
  const { t } = useTranslation('shared');
  const [hoverValue, setHoverValue] = useState<number>(0);

  return (
    <div className={cx('flex max-w-md flex-col items-start', className)}>
      <div className="flex w-full justify-between">
        {RATING_VALUES.map((value) => (
          <div
            key={value}
            onClick={() => onClickRating(value)}
            onKeyDown={() => onClickRating(value)}
            onMouseEnter={() => setHoverValue(value)}
            onMouseLeave={() => setHoverValue(0)}
            data-testid={`feedback-panel-star-${value}`}
          >
            <label key={value} htmlFor={`star${value}`}>
              <span className="sr-only">{t('userFeedback_scaleRecommendation_label_star', { count: value })}</span>
              <input
                className="sr-only"
                id={`star${value}`}
                type="radio"
                name="feedbackPanelStarsRating"
                value={value}
                onChange={() => onClickRating(value)}
                onFocus={() => setHoverValue(value)}
                onBlur={() => setHoverValue(0)}
                checked={value === ratingValue}
              />
              <PhoenixIcon
                className={cx(
                  'h-8 w-8 transform-gpu cursor-pointer transition-colors duration-500',
                  value <= ratingValue && (!hoverValue || hoverValue === ratingValue) ? 'text-primary-orange' : 'text-secondary',
                  { 'text-secondary-dark': value <= hoverValue && hoverValue !== ratingValue }
                )}
                icon={starRatingIcon}
              />
            </label>
          </div>
        ))}
      </div>
      {feedbackLabels && (
        <div className="mt-4 flex w-full justify-between text-secondary-deep stihl-text-body-sm-reg tablet:stihl-text-body-default-reg">
          <span>{feedbackLabels[0]}</span>
          <span>{feedbackLabels[1]}</span>
        </div>
      )}
    </div>
  );
};
